import { type MultipleSurveyQuestion } from "posthog-js";
import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import classNames from "classnames";
import { questionToAnswerId } from "~/services/analytics/surveys/questionToAnswerId";
import { type SurveyResponses } from "./OpenQuestion";
import { translations } from "~/services/translations/translations";
import InputError from "~/components/formElements/inputs/error/InputError";

type MultipleChoiceQuestionProps = {
  question: MultipleSurveyQuestion;
  setResponses: Dispatch<SetStateAction<SurveyResponses | undefined>>;
  hasError?: boolean;
};

export const MultipleChoiceQuestion = ({
  question,
  setResponses,
  hasError = false,
}: MultipleChoiceQuestionProps) => {
  const [checkboxStates, setCheckboxStates] = useState(
    question.choices.map(() => false),
  );

  const firstCheckboxRef = useRef<HTMLInputElement>(null);
  const errorId = `${question.id}-error`;

  useEffect(() => {
    if (hasError) {
      firstCheckboxRef.current?.focus();
    }
  }, [hasError]);

  const onCheckboxClicked = (idx: number, choice: string) => {
    const checked = !checkboxStates[idx];
    setCheckboxStates((prev) => prev.map((c, i) => (i === idx ? checked : c)));
    setResponses((prev) => {
      const existingResponses =
        (prev?.[questionToAnswerId(question)] as string[]) ?? [];
      const newAnswers = checked
        ? [...existingResponses, choice]
        : existingResponses.filter((c) => c !== choice);

      return {
        ...prev,
        [questionToAnswerId(question)]: newAnswers,
      };
    });
  };

  return (
    <fieldset
      className={classNames("kern-fieldset flex flex-col", {
        "kern-fieldset--error": hasError,
      })}
      aria-invalid={hasError}
    >
      <legend className="kern-body font-semibold py-8!">
        {question.question}
      </legend>
      <div className="flex flex-col gap-16">
        {question.choices.map((choice, idx) => {
          const choiceName = choice.replaceAll(" ", "_");
          return (
            <label
              className="flex items-center gap-16 flex-nowrap"
              key={choiceName}
            >
              <input
                type="checkbox"
                checked={checkboxStates[idx]}
                name={choiceName}
                onChange={() => onCheckboxClicked(idx, choice)}
                ref={idx === 0 ? firstCheckboxRef : undefined}
                className={classNames("kern-form-check__checkbox", {
                  "kern-form-check__checkbox--error": hasError,
                })}
              />
              {choice}
            </label>
          );
        })}
        {hasError && (
          <InputError id={errorId}>
            {translations.feedback["validation-error"].de}
          </InputError>
        )}
      </div>
    </fieldset>
  );
};
